/**
 * Modal dialogs, built on the native `<dialog>` element.
 *
 * `<dialog open>` alone is not enough — `showModal()` is what gives the focus
 * trap, the Esc-to-close behaviour, the inert background and the top-layer
 * stacking that a hand-rolled overlay has to reimplement badly. The effect below
 * drives the element imperatively for exactly that reason.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import Icon from './Icons'

function Dialog({
  open,
  title,
  onClose,
  children
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}): React.JSX.Element {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) {
      el.showModal()
      // showModal() runs after React has already applied autoFocus, and it puts
      // focus on the first focusable child — which is the header's close button.
      // For a dialog that exists to take a value, that is the wrong place: focus
      // the field and select it so typing replaces the seeded name.
      const field = el.querySelector('input, textarea')
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
        field.focus()
        field.select()
      }
    }
    if (!open && el.open) el.close()
  }, [open])

  return (
    <dialog
      className="modal"
      ref={ref}
      // Esc and the close button both route through onClose, so the caller's state
      // is the single source of truth for whether the dialog is up.
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      // The backdrop is part of the dialog's own box, so a click that lands on the
      // element itself — rather than on the inner panel — is a click outside.
      onClick={(e) => {
        if (e.target === ref.current) onClose()
      }}
    >
      <div className="modal-panel">
        <div className="modal-head">
          <h2>{title}</h2>
          <div className="spacer" />
          <button type="button" className="icon" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        {children}
      </div>
    </dialog>
  )
}

/** Text input dialog — the replacement for window.prompt(). */
export function PromptModal({
  open,
  title,
  label,
  initial = '',
  confirmLabel = 'Save',
  placeholder,
  onCancel,
  onSubmit
}: {
  open: boolean
  title: string
  label: string
  initial?: string
  confirmLabel?: string
  placeholder?: string
  onCancel: () => void
  onSubmit: (value: string) => void
}): React.JSX.Element {
  const [value, setValue] = useState(initial)

  // Reopening for a different device must not show the previous one's name.
  useEffect(() => {
    if (open) setValue(initial)
  }, [open, initial])

  const trimmed = value.trim()

  return (
    <Dialog open={open} title={title} onClose={onCancel}>
      <form
        className="stack-tight"
        onSubmit={(e) => {
          e.preventDefault()
          if (trimmed) onSubmit(trimmed)
        }}
      >
        <label>{label}</label>
        <input
          autoFocus
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="modal-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={!trimmed}>
            {confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

/** Yes/no dialog for an action worth a second look. */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  danger = false,
  onCancel,
  onConfirm
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel?: string
  danger?: boolean
  onCancel: () => void
  onConfirm: () => void
}): React.JSX.Element {
  return (
    <Dialog open={open} title={title} onClose={onCancel}>
      <div className="stack-tight">
        <p className="hint">{body}</p>
        <div className="modal-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={danger ? 'danger' : 'primary'}
            autoFocus
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
